/**
 * File Database
 *
 * IndexedDB storage for workspace file entries using Dexie.
 * Caches the full flat file list per workspace so folder navigation
 * is pure client-side filtering with zero extra API calls.
 *
 * Schema:
 * - files table: Stores FileEntry records
 * - Primary key: id
 * - Indexes: filePath, roomId, [roomId+filePath] (compound for workspace+path queries)
 */

import Dexie, { type Table } from 'dexie';
import type { FileEntry } from '@bloomstack/apis/files';

/**
 * Stored file entry — identical shape to FileEntry from apis.
 * roomId is required here (always present for workspace files).
 */
export type StoredFileEntry = FileEntry & {
  roomId: string;
};

export class FileDatabase extends Dexie {
  files!: Table<StoredFileEntry, string>;

  constructor() {
    super('AIMysticFiles');
    this.version(1).stores({
      files: 'id, filePath, roomId, [roomId+filePath]',
    });
  }
}

// Export singleton instance
export const fileDb = new FileDatabase();

// ============================================================================
// CRUD OPERATIONS
// ============================================================================

/**
 * Upsert a single file entry.
 */
export async function putFile(entry: FileEntry): Promise<void> {
  if (!entry.roomId) return;
  await fileDb.files.put(entry as StoredFileEntry);
}

/**
 * Upsert multiple file entries (for initial load).
 */
export async function bulkPutFiles(entries: FileEntry[]): Promise<void> {
  const storable = entries.filter((e): e is StoredFileEntry => !!e.roomId);
  await fileDb.files.bulkPut(storable);
}

/**
 * Delete a single file by ID.
 */
export async function deleteFileById(id: string): Promise<void> {
  await fileDb.files.delete(id);
}

/**
 * Delete all files belonging to a workspace (used before re-sync).
 */
export async function deleteFilesByRoomId(roomId: string): Promise<void> {
  await fileDb.files.where('roomId').equals(roomId).delete();
}

/**
 * Get all files for a workspace, ordered by filePath.
 */
export async function getFilesByRoomId(roomId: string): Promise<StoredFileEntry[]> {
  return fileDb.files.where('roomId').equals(roomId).sortBy('filePath');
}
